import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Shield, Banknote, CreditCard, Landmark, Bitcoin, Layers } from "lucide-react";

const partners = [
  "STRIPE", "PLAID", "VISA", "MASTERCARD", "ACH NETWORK", "COINGECKO",
  "FDIC INSURED*", "BANK GRADE 256-BIT", "SOC 2 TYPE II", "PCI DSS",
];

const Landing = () => {
  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] grain">
      {/* Header */}
      <header className="glass sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="home-logo">
            <div className="h-8 w-8 bg-[hsl(var(--primary))] grid place-items-center">
              <span className="text-white font-display text-lg leading-none">N</span>
            </div>
            <div className="font-display text-lg tracking-tight">NASHUKA</div>
          </Link>
          <nav className="hidden md:flex items-center gap-8 overline">
            <a href="#products" className="hover:text-[hsl(var(--primary))]">Products</a>
            <a href="#stack" className="hover:text-[hsl(var(--primary))]">Why us</a>
            <a href="#crypto" className="hover:text-[hsl(var(--primary))]">Crypto</a>
            <a href="#trust" className="hover:text-[hsl(var(--primary))]">Trust</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm hover:underline" data-testid="header-login">Sign in</Link>
            <Link
              to="/register"
              className="bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm hover:bg-[hsl(var(--primary))]/90 flex items-center gap-2"
              data-testid="header-get-started"
            >
              Open account <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 -z-0">
          <img
            src="https://images.pexels.com/photos/18502918/pexels-photo-18502918.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt=""
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10 pt-24 pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 fade-up">
            <div className="overline mb-6 inline-flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" /> Personal Banking · Built for you
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              A complete banking solution,<br />
              where you <span className="text-[hsl(var(--accent))]">control</span> your finances.
            </h1>
            <p className="text-lg max-w-2xl mt-8 text-[hsl(var(--text-muted))] leading-relaxed">
              Checking, savings, debit and credit cards, loans, and crypto — all in one place.
              Move money in seconds, freeze a card in a tap, and grow your wealth on your terms.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/register"
                className="bg-[hsl(var(--primary))] text-white px-6 py-3 text-sm hover:bg-[hsl(var(--primary))]/90 flex items-center gap-2"
                data-testid="hero-cta-register"
              >
                Open an account <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login"
                className="border border-[hsl(var(--primary))] text-[hsl(var(--primary))] px-6 py-3 text-sm hover:bg-[hsl(var(--surface))]"
                data-testid="hero-cta-login"
              >
                Sign in to dashboard
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 fade-up d2">
            <div className="card-tech p-6">
              <div className="overline mb-4">By the numbers</div>
              <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                <div>
                  <div className="overline">Total deposits</div>
                  <div className="font-mono text-2xl mt-1">$3.4B</div>
                </div>
                <div>
                  <div className="overline">Cards in wallets</div>
                  <div className="font-mono text-2xl mt-1">128,402</div>
                </div>
                <div>
                  <div className="overline">Loans funded</div>
                  <div className="font-mono text-2xl mt-1">$812M</div>
                </div>
                <div>
                  <div className="overline">Transfer speed</div>
                  <div className="font-mono text-2xl mt-1">Instant</div>
                </div>
              </div>
              <div className="divider-h my-5" />
              <div className="flex items-center justify-between">
                <span className="overline">Customer support</span>
                <span className="font-mono text-sm">24 / 7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner marquee */}
      <section className="border-y border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-hidden">
        <div className="marquee-track flex gap-16 py-5 whitespace-nowrap">
          {[...partners, ...partners].map((p, i) => (
            <span key={i} className="overline text-[hsl(var(--text-muted))]">{p}</span>
          ))}
        </div>
      </section>

      {/* Products grid */}
      <section id="products" className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-4">
            <div className="overline mb-3">01 · What's inside</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              One account.<br />Everything you need.
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-[hsl(var(--text-muted))] text-base leading-relaxed self-end">
            From everyday spending to big-picture planning — checking, savings, cards, loans,
            and crypto all live in one trusted place, with no surprise fees.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[hsl(var(--border))] border border-[hsl(var(--border))]">
          {[
            { icon: Banknote, name: "Checking & Savings", desc: "Spend, save, and earn interest — no monthly fees, no minimums." },
            { icon: CreditCard, name: "Debit & Credit Cards", desc: "Tap, swipe, or shop online. Freeze and unfreeze in a tap if your card goes missing." },
            { icon: Landmark, name: "Loans That Make Sense", desc: "Borrow for what matters — personal, auto, home, or business — with clear rates." },
            { icon: Bitcoin, name: "Crypto, Built In", desc: "Buy Bitcoin, Ethereum, USDC and Solana at live prices, funded from your checking." },
          ].map((m, i) => (
            <div key={i} className="bg-[hsl(var(--surface))] p-8 hover:bg-[hsl(var(--surface-alt))] transition-colors fade-up" style={{ animationDelay: `${i * 0.06}s` }}>
              <m.icon className="w-7 h-7 text-[hsl(var(--accent))]" strokeWidth={1.4} />
              <h3 className="font-display text-xl mt-6">{m.name}</h3>
              <p className="text-sm text-[hsl(var(--text-muted))] mt-2">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Crypto block */}
      <section id="crypto" className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-stretch">
          <div className="md:col-span-7">
            <img
              src="https://images.pexels.com/photos/3137072/pexels-photo-3137072.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=700&w=1100"
              alt="Crypto"
              className="w-full h-[420px] object-cover grayscale"
            />
          </div>
          <div className="md:col-span-5 flex flex-col justify-center">
            <div className="overline mb-3">02 · Onchain ↔ Bank</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Stablecoins meet a real general ledger.
            </h2>
            <p className="text-[hsl(var(--text-muted))] mt-6">
              Convert fiat balances into BTC, ETH, USDC or SOL with live CoinGecko prices.
              Funds settle to the same checking account—no separate exchange account required.
            </p>
            <Link
              to="/register"
              className="mt-8 inline-flex items-center gap-2 text-[hsl(var(--primary))] hover:text-[hsl(var(--accent))]"
              data-testid="crypto-cta"
            >
              Start trading <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Nashuka */}
      <section id="stack" className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="overline mb-3">03 · Why Nashuka</div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">Banking should feel like yours.</h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { k: "01", t: "Instant transfers", d: "Move money between your accounts and to anyone you know — no waiting days for it to land." },
              { k: "02", t: "Built-in safety", d: "Bank-grade encryption, freeze cards anytime, and real people on support when you need them." },
              { k: "03", t: "Smart cards", d: "Debit and credit cards with spending controls and instant freeze, all from your dashboard." },
              { k: "04", t: "All in one place", d: "Accounts, loans, and crypto behind a single sign-in — no jumping between apps." },
            ].map((b, i) => (
              <div key={i} className="border-l border-[hsl(var(--border))] pl-6">
                <div className="overline">{b.k}</div>
                <h3 className="font-display text-lg mt-2">{b.t}</h3>
                <p className="text-sm text-[hsl(var(--text-muted))] mt-2">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section id="trust" className="bg-[hsl(var(--primary))] text-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <div className="overline text-white/60 mb-3">04 · Trust</div>
            <h2 className="font-display text-3xl md:text-5xl leading-tight">
              Move money like you mean it.
            </h2>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-y-6 self-center">
            <div>
              <div className="font-mono text-3xl">256-bit</div>
              <div className="overline text-white/60">Bank-grade security</div>
            </div>
            <div>
              <div className="font-mono text-3xl">Instant</div>
              <div className="overline text-white/60">Transfers between accounts</div>
            </div>
            <div>
              <div className="font-mono text-3xl">FDIC*</div>
              <div className="overline text-white/60">Deposit protection</div>
            </div>
            <div>
              <div className="font-mono text-3xl">24 / 7</div>
              <div className="overline text-white/60">Always accessible</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <h2 className="font-display text-4xl md:text-5xl leading-tight">
            Open your account in 60 seconds.
          </h2>
          <p className="text-[hsl(var(--text-muted))] mt-4 max-w-xl">
            Sign up and we'll provision a checking account with a $100 welcome credit so you can
            experience real money movement, card issuance and crypto trading instantly.
          </p>
        </div>
        <div className="md:col-span-5 flex md:justify-end">
          <Link
            to="/register"
            className="bg-[hsl(var(--accent))] text-white px-8 py-4 text-base hover:bg-[hsl(var(--accent))]/90 flex items-center gap-2"
            data-testid="footer-cta-register"
          >
            Get started <Layers className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[hsl(var(--border))] py-10">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4">
          <div className="overline">© 2026 Nashuka Financial Services — Personal banking, reimagined.</div>
          <div className="overline text-[hsl(var(--text-muted))]">
            * Deposits eligible for FDIC insurance through partner bank in production.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
